import { useCallback, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ThreadDownloadDialog } from './thread-download-dialog';
import { Permission } from '../../../../../../../../../../../../backend/src/auth/permissions/permission';
import { DocumentDownloadOptions } from '../../../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { ThreadExportEvent } from '@/api/hooks/useThreadExportEvents';
import { APIBaseChronos } from '@/api/hosts';
import { useDownloadKimAnswer } from '@/api/mutations/useDownloadKimAnswer';
import { useDuplicateManualThread } from '@/api/mutations/useDuplicateManualThread';
import { useTogglePublishedThread } from '@/api/mutations/useTogglePublishedThread';
import { useGetThreadStatus } from '@/api/queries/useGetThreadStatus';
import { cleanReferencesAndCopyToClipboard } from '@/constants/referencing';
import { useUserContext } from '@/Contexts/User';
import { getTitleFromFlowType } from '@/helpers/kimDisplay';
import { PermissionGate } from '@/hooks/useHasPermission';
import { CopyButton } from '@/new/components/copy-button';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';
import { DownloadButton } from '@/new/components/download-button';
import { ExportReadyDialog } from '@/new/components/export-ready-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { Artifact, ConfidenceLevelObject, KimMode } from '@/types';

interface AnswerHeaderProps {
  confidenceLevel: ConfidenceLevelObject;
  content: string;
  caseId: string;
  threadId: string;
  messageId: string;
  artifact: Artifact | undefined;
  flowType: KimMode | undefined;
  isLoadingMessages: boolean;
  onEdit?: () => void;
}

export function AnswerHeader({
  confidenceLevel,
  content,
  caseId,
  threadId,
  messageId,
  artifact,
  flowType,
  isLoadingMessages,
  onEdit,
}: AnswerHeaderProps) {
  const [downloadStart, setDownloadStart] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);

  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;
  const navigate = useNavigate();

  const { user } = useUserContext();
  const { downloadAnswer, downloadAnswerWithDocuments } = useDownloadKimAnswer(
    matterId,
    subsetId,
    threadId,
    messageId,
    artifact,
  );
  const { data: threadData } = useGetThreadStatus({ matterId, subsetId, threadId });
  const { mutate: toggleThreadPublished, isLoading: isToggling } = useTogglePublishedThread(matterId, subsetId);
  const { mutate: duplicateThread, isLoading: isDuplicating } = useDuplicateManualThread();

  const handleDownloadClick = () => {
    // If there's an artifact, download directly without showing modal
    if (artifact) {
      handleDownloadAnswer();
    } else {
      // Show modal for formatting options
      setShowDownloadModal(true);
    }
  };

  const handleDownloadAnswer = async (options?: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
    trackCustomEvent(TrackingEventsV2.ThreadDownload, { caseId, threadId, includeSourceDocuments });
    setShowDownloadModal(false);

    if (includeSourceDocuments) {
      // If an export is already in progress, reopen the progress modal
      if (currentExportId) {
        setShowExportProgressModal(true);
        return;
      }

      // Start async export job
      try {
        setExportError(undefined);
        const result = await downloadAnswer(options, true);
        if (result && 'exportId' in result && result.exportId) {
          setCurrentExportId(result.exportId);
        }
        setIsCreatingExport(true);
        setShowExportProgressModal(true);
      } catch (error) {
        console.error('[AnswerHeader] Export failed:', error);
        setExportError(error instanceof Error ? error.message : 'Failed to start export');
        setShowExportProgressModal(true);
      }
    } else {
      // Original synchronous download
      setDownloadStart(true);

      try {
        await downloadAnswer(options);
      } catch (error) {
        console.error('Download failed:', error);
        toast.error('Download failed. Please try again.');
      } finally {
        // Reset state
        setDownloadStart(false);
        setDownloadComplete(true);
        setTimeout(() => setDownloadComplete(false), 1000);
      }
    }
  };

  const handleExportDownload = useCallback(async () => {
    if (!currentExportId) return;
    try {
      await downloadAnswerWithDocuments(currentExportId);
      toast.success('Download started');
      setShowExportProgressModal(false);
      setIsCreatingExport(false);
      setCurrentExportId(null);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  }, [currentExportId, downloadAnswerWithDocuments]);

  const handleCloseExportModal = () => {
    setShowExportProgressModal(false);
    setIsCreatingExport(false);
  };

  const copyPageLinkToClipboard = () => {
    trackCustomEvent(TrackingEventsV2.ThreadCopyPageLink, { caseId, threadId });
    navigator.clipboard.writeText(window.location.href);
    toast.info('Link to page copied to clipboard');
  };

  const handleTogglePublished = () => {
    if (!threadData?.isPublished) {
      trackCustomEvent(TrackingEventsV2.ThreadThreadPublished, { caseId, threadId });
    }
    toggleThreadPublished(threadId, {
      onSuccess: ({ isPublished }) => {
        toast.success(`Thread ${isPublished ? 'published' : 'unpublished'} successfully`);
      },
      onError: () => {
        toast.error('Failed to toggle thread published status');
      },
    });
  };

  const handleDuplicate = () => {
    duplicateThread(
      { matterId, subsetId, threadId },
      {
        onSuccess: (data) => {
          toast.success('Answer duplicated successfully');
          navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${data.threadId}`);
        },
        onError: () => {
          toast.error('Failed to duplicate answer');
        },
      },
    );
  };

  const buttonsDisabled = isLoadingMessages || confidenceLevel.text === 'Low';

  return (
    <div className="flex h-14 items-center justify-between gap-1 border-b border-gray-100 bg-gray-50/50 px-6 py-3">
      <div className="flex items-center gap-3">
        <span className="iconify lucide--lightbulb text-brandSecondary size-5" />
        <h3 className="text-sm font-medium text-gray-800">{getTitleFromFlowType(flowType)}</h3>

        <Tooltip>
          <TooltipTrigger className="ml-2 flex w-fit items-center gap-1.5 rounded-md border bg-white px-2.25 py-1 text-xs text-gray-600">
            <Icon className="lucide--info" />
            <p className="-mb-0.25">Review sources to verify {getTitleFromFlowType(flowType).toLowerCase()}</p>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-sm p-2 px-3 text-gray-800" sideOffset={8}>
            Kim has undergone extensive testing to increase the accuracy of the answers. However, as with all generative
            AI systems, in complex scenarios it could return an inaccurate answer. Crucially, you should check the
            source fact and document in the citation to verify the answer's completeness or accuracy.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center justify-between gap-2">
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={copyPageLinkToClipboard}>
                <Icon className="lucide--link h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy link to this page.</p>
            </TooltipContent>
          </Tooltip>

          {flowType !== KimMode.ExtractionFlow && (
            <CopyButton
              copyHandler={() => {
                trackCustomEvent(TrackingEventsV2.ThreadCopyAnswer, { caseId, threadId });
                cleanReferencesAndCopyToClipboard(content);
                toast.success('Copied to clipboard');
              }}
              tooltipText="Copy answer to clipboard."
            />
          )}

          <PermissionGate permission={Permission.ThreadDownload} uiMode="disabled">
            <DownloadButton
              handleDownload={handleDownloadClick}
              downloadStart={downloadStart}
              downloadComplete={downloadComplete}
              tooltipText="Download answer."
              disabled={buttonsDisabled}
            />
          </PermissionGate>
        </>

        {flowType === KimMode.ManualFlow && user?.organisation?.isLabellingOrg && onEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={onEdit} disabled={buttonsDisabled}>
                <Icon className="lucide--pencil h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit manual answer</p>
            </TooltipContent>
          </Tooltip>
        )}

        {flowType === KimMode.ManualFlow && user?.organisation?.isLabellingOrg && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={handleDuplicate} disabled={buttonsDisabled || isDuplicating}>
                <Icon className="lucide--copy h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Duplicate manual answer</p>
            </TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <PermissionGate permission={Permission.ThreadPublish} uiMode="disabled">
              <Button
                variant="outline"
                size="sm"
                className={`text-xs ${threadData?.isPublished ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800' : ''}`}
                onClick={handleTogglePublished}
                disabled={isToggling || buttonsDisabled || threadData?.createdById !== user?.id}
              >
                <span className="iconify lucide--users h-3 w-3" />
                <p className="text-xs">{threadData?.isPublished ? 'Unpublish' : 'Publish'}</p>
              </Button>
            </PermissionGate>
          </TooltipTrigger>
          <TooltipContent className="max-w-56">
            <p className="text-center">
              {threadData?.createdById !== user?.id
                ? 'You can only publish threads you have created.'
                : threadData?.isPublished
                  ? 'Remove from the list of shared questions.'
                  : 'Add to the list of shared questions.'}
            </p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              onClick={() => {
                trackCustomEvent(TrackingEventsV2.ThreadConfidenceLevel, { caseId, threadId });
              }}
              className={`relative hover:cursor-pointer ${confidenceLevel.backgroundColor} ${confidenceLevel.textColor} rounded-md border text-xs ${confidenceLevel.borderColor} flex items-center gap-2 hover:${confidenceLevel.backgroundColor} hover:${confidenceLevel.borderColor}`}
            >
              <span>
                Confidence: <b className="font-semibold">{confidenceLevel.text}</b>
              </span>
            </Button>
          </TooltipTrigger>

          <TooltipContent className="mr-4" side="bottom">
            {confidenceLevel.tooltip}
          </TooltipContent>
        </Tooltip>
      </div>

      <ThreadDownloadDialog
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadAnswer}
        isLoading={downloadStart}
      />

      <GenericExportProgressModal<ThreadExportEvent>
        isOpen={showExportProgressModal}
        onClose={handleCloseExportModal}
        exportConfig={{
          progressEndpoint: `${APIBaseChronos}/api/matter/${matterId}/subset/${caseId}/thread/${threadId}/message/${messageId}/export-progress`,
          exportId: currentExportId,
        }}
        itemName="Kim Answer"
        onDownload={handleExportDownload}
        isCreating={isCreatingExport}
        error={exportError}
      />

      <ExportReadyDialog
        onDownload={async (exportId) => {
          await downloadAnswerWithDocuments(exportId);
        }}
        dialogTitle="Export Ready"
        message="Your Kim Answer export with source documents is ready to download."
      />
    </div>
  );
}
